import { FormContainer, ImageField, InputField, SelectEnumField, VideoField } from '@app/lib/form'
import { Input } from '@app/lib/ui/input'
import { Component, Field, HasOne, useEntity, useEntityBeforePersist, useField } from '@contember/interface'
import { FormFieldScope } from '@contember/react-form'
import { useState } from 'react'

const extractVimeoUrl = (input: string): string => {
	const iframeMatch = input.match(/<iframe[^>]+src="([^"]+)"/)
	if (iframeMatch?.[1]) {
		const srcUrl = iframeMatch[1].replace(/&amp;/g, '&')
		return srcUrl.split('?')[0] ?? srcUrl
	}
	if (input.startsWith('http')) {
		return input.split('?')[0] ?? input
	}
	return input
}

const VimeoEmbedUrlInput = Component<{ field: string }>(
	({ field }) => {
		const fieldAccessor = useField<string>(field)

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const cleanUrl = extractVimeoUrl(e.target.value)
			fieldAccessor.updateValue(cleanUrl)
		}

		return (
			<FormFieldScope field={field}>
				<FormContainer label="Vimeo embed URL">
					<Input
						type="text"
						value={fieldAccessor.value ?? ''}
						onChange={handleChange}
						className="max-w-md"
						placeholder="Paste iframe embed code or Vimeo URL"
					/>
				</FormContainer>
			</FormFieldScope>
		)
	},
	({ field }) => <Field field={field} />,
)

export const OptionalVideoInput = Component<{ videoField: string }>(
	({ videoField }) => {
		const entityAccessor = useEntity()
		const videoFieldAccessor = entityAccessor.getEntity(videoField)
		const [useVideo, setUseVideo] = useState(!!videoFieldAccessor.idOnServer)
		useEntityBeforePersist(() => {
			if (!useVideo && videoFieldAccessor.idOnServer) {
				entityAccessor.disconnectEntityAtField(videoField)
			}
		})
		return (
			<div className="flex flex-col gap-2">
				<div className="flex items-center gap-2">
					<input id="useVideoHero" type="checkbox" checked={useVideo} onChange={e => setUseVideo(e.target.checked)} />
					<label
						htmlFor="useVideoHero"
						className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 data-[invalid]:text-destructive text-left"
					>
						Use Hero Video
					</label>
				</div>
				{useVideo ? (
					<>
						<VideoField
							label="Hero Video"
							baseField="video"
							urlField="url"
							widthField="width"
							heightField="height"
							fileNameField="fileName"
							fileTypeField="fileType"
							fileSizeField="fileSize"
						/>
						<HasOne field="video">
							<SelectEnumField field="variant" label="Video variant" options={{ mood: 'Mood', informative: 'Informative' }} />
							<InputField field="youtubeEmbedVideo" label="YouTube embed code / URL" />
							<VimeoEmbedUrlInput field="vimeoEmbedUrl" />
							<InputField field="vimeoSrcUrl" label="Vimeo mp4 source URL" />
							<ImageField
								label="Hero Video Thumbnail"
								baseField="thumbnail"
								urlField="url"
								widthField="width"
								heightField="height"
								fileNameField="meta.fileName"
								fileTypeField="meta.fileType"
								fileSizeField="meta.fileSize"
							/>
						</HasOne>
					</>
				) : undefined}
			</div>
		)
	},
	({ videoField }) => (
		<>
			<HasOne field={videoField}>
				<Field field="variant" />
				<Field field="youtubeEmbedVideo" />
				<Field field="vimeoEmbedUrl" />
				<Field field="vimeoSrcUrl" />
				<ImageField
					label="Video thumbnail"
					baseField="thumbnail"
					urlField="url"
					widthField="width"
					heightField="height"
					fileNameField="meta.fileName"
					fileTypeField="meta.fileType"
					fileSizeField="meta.fileSize"
				/>
			</HasOne>
			<VideoField
				baseField="video"
				urlField="url"
				widthField="width"
				heightField="height"
				fileNameField="fileName"
				fileTypeField="fileType"
				fileSizeField="fileSize"
			/>
		</>
	),
)
